import {
  AdditionalNWDeviceAttributes,
  DeviceTypes,
  MapLike,
  Networks,
  Position,
  ValidBusinessObject,
  isValidBusinessObject,
  stripNode,
} from '@iotv/iotv-v3-types'
import { ValidModelObject } from '../classDependentTypes'
import { Device } from '../model/Device/Device'
import { DatastoreObjectType, RegionalParameterRevision, RegionalPlan } from '../types'

export enum DeviceProvisioningState {
  NOT_INVENTORIZED = 'NOT_INVENTORIZED',
  INVENTORY = 'INVENTORY',
  ALLOCATED = 'ALLOCATED',
}

export enum DeviceState {
  UNKNOWN = 'UNKNOWN',
  MANUFACTURING = 'MANUFACTURING',
  COMMISSIONED = 'COMMISSIONED',
  DECOMMISSONED = 'DECOMMISSONED',
  SUSPENDED = 'SUSPENDED',
  CALIBRATED = 'CALIBRATED',
  TRANSITIN = 'TRANSITIN',
  TRANSITOUT = 'TRANSITOUT',
  WITHCUSTOMER = 'WITHCUSTOMER',
  WITHENGINEER = 'WITHENGINEER',
  INVENTORY = 'INVENTORY',
  ORDEREDCUSTOMER = 'ORDEREDCUSTOMER',
  ORDERED = 'ORDERED',
  RETIRED = 'RETIRED',
  JOINED = 'JOINED',
}

export type DeviceProvisioningRecordType = {
  gatewayId?: string
  provisionDate: number
  network?: string
  manager?: string
  groupNames?: string[]
  reason?: string
  provisioningState?: DeviceProvisioningState
  inventoryCustomer?: ValidBusinessObject // was DatastoreObjectType;
  allocationCustomer?: ValidBusinessObject // was DatastoreObjectType;
  provisioningUser?: DatastoreObjectType
  lastAssignedCustomer?: ValidModelObject<'Customer'>
}

export interface Maybe extends Device {}

export type MandatoryDeviceKeysType = 'eui' | 'appID' | 'appKey' | 'networkKey' | 'deviceId' | 'deviceType' | 'appEui'

export const MandatoryDeviceKeys: MandatoryDeviceKeysType[] = ['eui', 'appID', 'appKey', 'networkKey', 'deviceId', 'deviceType', 'appEui']

export type DeviceMandatoryProps = {
  /** the LWS enumeration for the type of Network - some Networks types eg TTNE and Chirpstack have more than one application instance */
  networkKey: string
  /** the LoRaWAN AppKey of the device - a uuid */
  appKey: string
  /** the LWS type of the device */
  deviceType: DeviceTypes
  /** the id or equivalent (not eui) of the device from the LNS Application Server */
  deviceId: string
  /** the application id or name from the LNS Application Server */
  appID: string
  /** the device eui from the device and LNS Application Server */
  eui: string
  /** the LoRaWAN app eui / join eui */
  appEui: string
}

export type DeviceProps = DeviceMandatoryProps & {
  /** the sk of the lns resolved in NetworkData  */
  lns?: string
  error?: MapLike<0 | 1>

  dailyMessageCount: number
  dailyMessageHistory: number[]
  dailyFCntSkip: number
  fCntSkipHistory: number[]
  yesterdaysMessageCntTotal: number
  yesterdaysFCntJumpTotal: number
  deviceVersion: string
  //Groups
  deviceTypeName?: string
  deviceType?: DeviceTypes
  deviceDroppable?: boolean
  deviceSubType: string
  regionalPlan: RegionalPlan
  regionalParameterRevision: RegionalParameterRevision
  /** mA/h */
  batteryCapacity: number
  billingGroup: string
  nasSourceIp?: string
  lastGatewayEui?: string
  isAWOL: boolean
  lastSeen: number
  lastSendPeriod: number
  fCnt: number
  fCntInc: number
  customer: string
  firmwareLevel: string
  batteryLevel: number
  provisioning: DeviceProvisioningRecordType[] // most recent first so that can user provisioning[0] in queries
  dropped?: boolean
  deviceTime?: number
  accelerometerThreshold?: number
  location?: Position
  messageJoinCount: number
  messageUplinkCount: number // deprecate in favour of fcnt
  messageDownlinkCount: number
  messageFailureRate: number
  messageLast: number
  messagePeriodDefault: number
  messagePeriodActual: number
  /** the LWS state */
  state: DeviceState
} & Partial<AdditionalNWDeviceAttributes>

export type DevicePropsOptional = Partial<DeviceProps>

export interface IDeviceProps extends DeviceProps {}

export interface IDevice extends Partial<DeviceProps> {}

export type LNSResolvableDeviceObject = Omit<DeviceMandatoryProps, 'appKey' | 'appEui' | 'deviceType'> & ValidModelObject<'Device'>

/** type check for LNS resolution and downlink sending, to deal with legacy LNS devices where appKey, appEui are not recorded */
export const isLNSResolveableDeviceObject = (vob: any): vob is ValidDeviceObject => {
  const { eui, networkKey, deviceId, appID } = (vob as LNSResolvableDeviceObject) ?? {}
  return !!(isValidBusinessObject(vob) && eui === vob.id && appID && networkKey && (networkKey !== Networks.AWS_LPWAN || deviceId))
}

export type ValidDeviceObject = DeviceMandatoryProps & ValidModelObject<'Device'>

/** returns boolen indicating the device has all the key attributes required for system processing.
 * Checks on objects attributes: eui, appKey, networkKey or deviceId, deviceType
 * Note state is no longer mandatory, it is only required for state management.
 */
export const isValidDeviceObject = (vob: any): vob is ValidDeviceObject => {
  const { eui, appKey, networkKey, deviceId, deviceType, appEui, appID } = (vob as DeviceMandatoryProps) ?? {}

  return !!(isValidBusinessObject(vob) && eui === vob.id && appKey && appEui && networkKey && (networkKey !== Networks.AWS_LPWAN || deviceId) && deviceType)
}

/** returns a stripped device object which can be used to retrieve the full current version and has all necessary keys for system processing
 * used in DeviceControlTransaction to minimize storage.
 */
export const getMinimalValidDeviceObject = (item: any): ValidDeviceObject | null => {
  if (isValidDeviceObject(item)) {
    return stripNode(item, MandatoryDeviceKeys) as ValidDeviceObject
  }
  return null
}
